/// <amd-module name="Wit/Common/ColumnOptionsPanel/AggregationModeControl" />

import * as React from "react";
import { IBacklogLevelConfiguration } from "Agile/Common/Redux/BacklogConfiguration/BacklogConfigurationContracts";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { localeIgnoreCaseComparer, startsWith } from "VSS/Core/Util/String";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { Dropdown } from "VSSUI/Dropdown";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { ICommonBacklogContentService } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { BacklogFieldTypes, RollupAggregation } from "Wit/Common/BacklogContentService/RollupHelpers";
import * as Resources from "Wit/Common/ColumnOptionsPanel/Resources";
import { FieldType } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { IWorkItemTypesService } from "Wit/Common/WorkItemTypesService/IWorkItemTypesService";
import { IFieldProjectData, IWorkItemTypeData } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItemDataManager } from "Wit/WorkItemDataManager/WorkItemDataManager";

export interface AggregationField {
    aggregationField?: string | undefined;
    aggregationFieldDisplayName?: string | undefined;
}

export interface AggregationSelection extends AggregationField {
    aggregation: RollupAggregation;
}

export interface AggregationModeControlProps {
    onAggregationSelectionChanged: (aggregation: AggregationSelection, isValidField: boolean) => void;
    defaultAggregationMode: RollupAggregation;

    /**
     * Id of a backlog to get numeric fields for. Mutually exclusive with workItemTypeName.
     */
    backlogLevelId?: string;

    /**
     * Name of a work item type to get numeric fields for. Mutually exclusive with backlogLevelId.
     */
    workItemTypeName?: string;
}

interface AggregationModeControlState {
    aggregationMode: RollupAggregation;
    aggregationField?: AggregationField;
    workItemTypes?: IWorkItemTypeData[];
}

export class AggregationModeControl extends TfsComponent<AggregationModeControlProps, AggregationModeControlState> {
    private aggregationTypeOptions: IListBoxItem[] = [
        {
            id: Resources.Count,
            text: Resources.Count
        },
        {
            id: Resources.Sum,
            text: Resources.Sum
        }
    ];
    private dropdownItems: IListBoxItem<{}>[] = [];
    private fieldProjectData: IFieldProjectData;

    constructor(props: AggregationModeControlProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            aggregationMode: this.props.defaultAggregationMode,
        };
    }

    public async componentDidMount() {
        if (!this.projectData) {
            return;
        }
        await this._populateFieldProjectData();

        const commonBacklogContentService = this.context.pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
        const workItemTypesService = this.context.pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");
        let workItemTypes = await workItemTypesService.getWorkItemTypeData(this.projectData.id);
        const visibleTypes = await workItemTypesService.getVisibleWorkItemTypes(this.projectData.id);
        workItemTypes = workItemTypes.filter(typeData => visibleTypes.find(item => item === typeData.name));
        const selectedWorkItemTypes = await this.getSelectedWorkItemTypes(workItemTypes);

        const backlogConfiguration = await commonBacklogContentService.getBacklogConfiguration();

        const effortFieldRefName = backlogConfiguration?.backlogFields.typeFields[BacklogFieldTypes.Effort];
        let effortField: IFieldEntry | undefined;

        const effortFieldDefinition = this.fieldProjectData.fields.find(field => field.referenceName === effortFieldRefName);
        selectedWorkItemTypes.forEach(wit => {
            if (!effortField) {
                if (wit.fields.find(item => item == effortFieldDefinition?.id)) {
                    effortField = effortFieldDefinition;
                }
            }
        });
        const aggregationField = effortField
            ? {
                  aggregationField: effortField.referenceName,
                  aggregationFieldDisplayName: effortField.name
              }
            : undefined;

        this.setState({
            aggregationField,
            workItemTypes
        });
        this.dropdownItems = await this.getAggregationFieldOptions();
    }

    public async componentDidUpdate(previousProps: AggregationModeControlProps) {
        if (this.props.workItemTypeName !== previousProps.workItemTypeName || this.props.backlogLevelId !== previousProps.backlogLevelId) {
            if (this.state.aggregationMode === RollupAggregation.Sum) {
                const hasValidField = await this.isFieldSelectionValid();
                if (!hasValidField) {
                    this.setState({
                        aggregationField: undefined,
                    });
                }
                this.dropdownItems = await this.getAggregationFieldOptions();
                this.props.onAggregationSelectionChanged({ ...this.state.aggregationField, aggregation: this.state.aggregationMode }, hasValidField);
            }
        }
    }

    public render(): JSX.Element {
        return (
            <div className="padding-16">
                <span className="aggregation-mode-control-label">{Resources.AggregationTypePickerLabel}</span>
                <div className="flex-row">
                    <Dropdown
                        ariaLabel={Resources.AggregationModeLabel}
                        className="aggregation-type"
                        items={this.aggregationTypeOptions}
                        onSelect={this.onTypeSelectionChanged}
                        placeholder={Resources.Count}
                    />
                    <div className="aggregation-of-text">{Resources.Of}</div>
                    {this.renderSecondaryMetricControl()}
                </div>
            </div>
        );
    }

    private renderSecondaryMetricControl(): JSX.Element {
        if (this.state.aggregationMode === RollupAggregation.Count) {
            return <div className="aggregation-workitems-text">{Resources.WorkItems}</div>;
        } else {
            return (
                <Observer dropdownItems={this.dropdownItems}>
                    {({ dropdownItems }: { dropdownItems: IListBoxItem[] }) => {
                        return (
                            <Dropdown
                                ariaLabel={Resources.AggregationFieldLabel}
                                className="aggregation-field"
                                items={dropdownItems}
                                onSelect={this.onFieldSelectionChanged}
                            />
                        );
                    }}
                </Observer>
            );
        }
    }

    private onTypeSelectionChanged = async (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<{}>): Promise<void> => {
        if (!item?.text) {
            return;
        }
        let mode = item.id === Resources.Count ? RollupAggregation.Count : RollupAggregation.Sum;

        this.setState({
            aggregationMode: mode
        });
        this.props.onAggregationSelectionChanged(
            {
                ...this.state.aggregationField,
                aggregation: mode
            },
            await this.isFieldSelectionValid()
        );
    };

    private onFieldSelectionChanged = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<{}>): void => {
        const aggregationField: AggregationField = {
            aggregationField: item.id,
            aggregationFieldDisplayName: item.text
        };
        this.setState({
            aggregationField
        });
        this.props.onAggregationSelectionChanged(
            {
                ...aggregationField,
                aggregation: this.state.aggregationMode
            },
            true
        );
    };

    private async isFieldSelectionValid(): Promise<boolean> {
        const validFieldSelections = await this.getAggregationFieldOptions();
        return validFieldSelections.some(
            option =>
                this.state.aggregationField && !localeIgnoreCaseComparer(option.id as string, this.state.aggregationField.aggregationField ?? "")
        );
    }

    private async getSelectedWorkItemTypes(workItemTypes?: IWorkItemTypeData[]): Promise<IWorkItemTypeData[]> {
        if (workItemTypes) {
            if (this.props.workItemTypeName) {
                return workItemTypes.filter(type => !localeIgnoreCaseComparer(type.name, this.props.workItemTypeName ?? ""));
            } else if (this.props.backlogLevelId) {
                const backlog = await this.getBacklogLevel(this.props.backlogLevelId);
                if (!backlog) {
                    return [];
                }
                return workItemTypes.filter(type => backlog.workItemTypes.find(item => String(item) === type.name));
            }
        }
        return [];
    }

    private async getBacklogLevel(backlogLevelId: string): Promise<IBacklogLevelConfiguration | undefined> {
        const commonBacklogContentService = this.context.pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");

        const backlogConfiguration = await commonBacklogContentService.getBacklogConfiguration();
        if (!backlogConfiguration) {
            return;
        }

        const backlogLevels = [...backlogConfiguration.portfolioBacklogs, backlogConfiguration.requirementBacklog, backlogConfiguration.taskBacklog];

        return backlogLevels.find(backlogLevel => backlogLevel.id === backlogLevelId);
    }

    private async getAggregationFieldOptions(): Promise<IListBoxItem[]> {
        const numericFields = await this.getNumericFields();

        return numericFields.map(field => {
            return {
                id: field.referenceName,
                text: field.name
            };
        });
    }

    private async getNumericFields(): Promise<IFieldEntry[]> {
        const selectedWorkItemTypes = await this.getSelectedWorkItemTypes(this.state.workItemTypes);
        if (selectedWorkItemTypes.length) {
            let allFields: IFieldEntry[] = [];
            const numFields: number[] = [];
            selectedWorkItemTypes.forEach(type => numFields.push(...type.fields));
            allFields = numFields
                .map(fieldId => this.fieldProjectData.fields.find(field => field.id === fieldId))
                .filter((item): item is IFieldEntry => !!item);
            return [
                ...new Set(
                    allFields.filter(f => {
                        return f && (f.type === FieldType.Integer || f.type === FieldType.Double) && !startsWith(f.referenceName, "System.");
                    })
                )
            ];
        }

        return [];
    }

    private _populateFieldProjectData = async (): Promise<void> => {
        const itemDataManager = this.context.pageContext.getService<IWorkItemDataManager>("IWorkItemDataManager");
        this.fieldProjectData = (await itemDataManager.beginGetFieldProjectData(this.projectData!.id)) ?? { projects: [], fields: [] };
    };
}
